// src/components/profile/NotificationSettings.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Card,
  CardContent,
  Button,
  Alert,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const NotificationSettings = () => {
  const { user, updateProfile } = useAuth();
  const [settings, setSettings] = useState({
    email: {
      events: true,
      security: true,
      updates: false,
      marketing: false,
      reminders: true
    },
    push: {
      events: true,
      security: true,
      updates: false,
      marketing: false,
      reminders: true
    },
    frequency: 'immediate', // immediate, daily, weekly
    quietHours: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSwitchChange = (category, type) => (event) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: event.target.checked
      }
    }));
  };

  const handleFrequencyChange = (event) => {
    setSettings(prev => ({
      ...prev,
      frequency: event.target.value
    }));
  };

  const handleQuietHoursChange = (event) => {
    setSettings(prev => ({
      ...prev,
      quietHours: event.target.checked
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await updateProfile({ notificationSettings: settings });
      setMessage({ 
        type: 'success', 
        text: 'Configuración de notificaciones guardada correctamente' 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al guardar la configuración' 
      });
    } finally {
      setSaving(false);
    }
  };

  const notificationTypes = [
    {
      key: 'events',
      icon: <EventIcon />,
      title: 'Eventos y competencias',
      description: 'Notificaciones sobre nuevos eventos, cambios de horario, etc.'
    },
    {
      key: 'security',
      icon: <SecurityIcon />,
      title: 'Seguridad',
      description: 'Alertas de inicio de sesión, cambios de contraseña, etc.'
    },
    {
      key: 'updates',
      icon: <NotificationsIcon />,
      title: 'Actualizaciones del sistema',
      description: 'Nuevas funcionalidades y mejoras'
    },
    {
      key: 'marketing',
      icon: <CampaignIcon />,
      title: 'Promociones y noticias',
      description: 'Ofertas especiales y novedades'
    },
    {
      key: 'reminders',
      icon: <ScheduleIcon />,
      title: 'Recordatorios',
      description: 'Recordatorios de entrenamientos, eventos próximos, etc.'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Configuración de Notificaciones
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Personaliza cómo y cuándo quieres recibir notificaciones
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Notificaciones por Email */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <EmailIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Notificaciones por Email
            </Typography>
          </Box>
          
          <List dense>
            {notificationTypes.map((type) => (
              <ListItem key={`email-${type.key}`}>
                <ListItemIcon>
                  {type.icon}
                </ListItemIcon>
                <ListItemText
                  primary={type.title}
                  secondary={type.description}
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.email[type.key]}
                    onChange={handleSwitchChange('email', type.key)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Notificaciones Push */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <NotificationsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Notificaciones Push
            </Typography>
          </Box>
          
          <List dense>
            {notificationTypes.map((type) => (
              <ListItem key={`push-${type.key}`}>
                <ListItemIcon>
                  {type.icon}
                </ListItemIcon>
                <ListItemText
                  primary={type.title}
                  secondary={type.description}
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.push[type.key]}
                    onChange={handleSwitchChange('push', type.key)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Configuraciones adicionales */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuraciones Adicionales
          </Typography>

          {/* Frecuencia de notificaciones */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">
              Frecuencia de notificaciones por email
            </FormLabel>
            <RadioGroup
              value={settings.frequency}
              onChange={handleFrequencyChange}
              row
            >
              <FormControlLabel
                value="immediate"
                control={<Radio />}
                label="Inmediata"
              />
              <FormControlLabel
                value="daily"
                control={<Radio />}
                label="Resumen diario"
              />
              <FormControlLabel
                value="weekly"
                control={<Radio />}
                label="Resumen semanal"
              />
            </RadioGroup>
          </FormControl>

          {/* Horario silencioso */}
          <FormControlLabel
            control={
              <Switch
                checked={settings.quietHours}
                onChange={handleQuietHoursChange}
                color="primary"
              />
            }
            label="Activar horario silencioso (10 PM - 8 AM)"
          />
        </CardContent>
      </Card>

      {/* Botón para guardar */}
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
};

export default NotificationSettings;